/* eslint-disable array-callback-return */
/* eslint-disable react/destructuring-assignment */
import { css } from '@emotion/react';
import { Col, Collapse, Icon, Input, notification, Select } from 'antd';
import moment from 'moment';
import Router, { withRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  requestCharts,
  requestSearchOptions,
  resetResults,
  resetSearchFilters,
  saveCompliassistSearch,
  setCurrentPage,
  setSearchandFetchResults,
  setSearchFilters,
  setSelectedSearch,
  updateSearchFilters,
} from '@/src/compliance/redux/complianceReducer';
import { getSelectedSearch } from '@/src/compliance/redux/selectors';
import { isSearchFiltersEmpty, search } from '@/src/compliance/utils';
import parseURL from '@/src/compliance/utils/parseURL';
import DateRangePicker from '@/src/shared/components/DateRangePicker';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import { form as formStyle } from '@/styles';
import style from '@/styles/utilities/searchFilters';
import { convertMomentToDate, disableDayAfter, validateCustomRange } from '@/utils/dateTimeHelpers';
import formatURL from '@/utils/formatURL';
import { checkIfValueInSaved } from '@/utils/formHelpers';
import isObjectEmpty from '@/utils/isObjectEmpty';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

import Buttons from './Buttons';

const filterColorOverride = css`
  background-color: #fff
`

const initialSearch = {
  FieldOptions: {
    Categories: [],
    Charts: [],
    States: [],
    SearchText: '',
    DateRanges: [null, null],
    Page: null,
    PageSize: null,
    SortField: null,
    SortOrder: null,
  },
};

const FIELD_NAMES = ['SearchText', 'States', 'Categories', 'Charts', 'DateRanges'];

const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const { Panel } = Collapse;

const SearchFilters = props => {
  const {
    form,
    filterExpanded,
    setSelection,
    setSelectionTags,
    searchFilterKey,
    handleFiltersCollapseChange,
    router,
    resetTags,
    includeDates,
    rootSearchURL,
  } = props;

  const dispatch = useDispatch();

  const {
    searchOptions: { FieldOptions, isLoadingOptions },
    searchFiltersValues,
  } = useSelector(state => state.compliance);
  const personId = useSelector(state => state.auth.userinfo.PersonID);

  const { getFieldDecorator, validateFieldsAndScroll } = form;
  const { Categories, Charts, States } = FieldOptions;

  let filteredCharts = [];

  const selectedCategoryValue = form.getFieldValue('Categories');

  filteredCharts = Charts.filter(chart => {
    return chart.Categories.map(cat => cat.CategoryId).includes(selectedCategoryValue);
  });

  const handleCategoryChange = async categoryId => {
    const chartValue = form.getFieldValue('Charts');

    if (Array.isArray(chartValue)) {
      chartValue.map(c => {
        if (
          !Charts.find(ch => ch.ChartId === Number(c))
            .Categories.map(cat => cat.CategoryId)
            .includes(categoryId)
        ) {
          form.setFieldsValue({ Charts: [] });
        }
      });
    }
  };

  const handleReset = () => {
    form.setFieldsValue({
      Categories: '',
      Charts: [],
      States: [],
      SearchText: '',
      DateRanges: [null, null],
    });
    dispatch(resetResults());
    dispatch(resetSearchFilters());
    resetTags();
    const formattedURL = rootSearchURL;
    Router.push(formattedURL, formattedURL, { shallow: true });
  };

  const updateSearch = (field, value) => {
    dispatch(updateSearchFilters({ field, value }));
  };

  const renderSelect = ({
    initialValue,
    placeholder,
    options,
    optId,
    optDescription,
    fieldName,
    header,
    extra = '',
    extraProps = {},
  }) => {
    const isActive = checkIfValueInSaved(initialValue);
    const activeProp = isActive ? { defaultActiveKey: '1' } : {};
    return (
      <Collapse {...filterCollapseProps} {...activeProp} css={filterColorOverride}>
        <Panel
          key="1"
          header={
            <Heading type={4} weight="bolder" theme="primaryGray">
              {header}
            </Heading>
          }
        >
          <Form.Item extra={extra}>
            {getFieldDecorator(fieldName, {
              initialValue,
            })(
              <Select
                css={formStyle.inputGray}
                size="large"
                placeholder={placeholder}
                optionFilterProp="children"
                showSearch
                allowClear
                onChange={(v, s) => {
                  setSelection(v, s, fieldName);
                  updateSearch(fieldName, v);
                }}
                {...extraProps}
              >
                {options.map(opt => (
                  <Select.Option key={`${opt[optId]}`} value={opt[optId]}>
                    {opt[optDescription]}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Panel>
      </Collapse>
    );
  };

  const { query, asPath } = router;
  const queryString = JSON.stringify(query);
  const fieldOptionString = JSON.stringify(FieldOptions);

  useEffect(() => {
    dispatch(requestSearchOptions());
  }, []);

  useEffect(() => {
    // WHEN COMPONENT FIRST MOUNTS PARSE THE URL IN THE BROWSER
    const updatedPayload = parseURL(FieldOptions, router);
    dispatch(setCurrentPage(1));

    if (isObjectEmpty(query)) {
      form.setFieldsValue({
        Categories: '',
        Charts: [],
        States: [],
        SearchText: '',
        DateRanges: [null, null],
      });
      dispatch(resetResults());
      dispatch(resetSearchFilters());
      resetTags();
    }
    if (query.searchId) {
      // Choose another savedSearch (searchId changed),
      // need to clear local state tags and reset form if touched
      const selectionTags = FIELD_NAMES.map(name => [null, null, name]);
      setSelectionTags(selectionTags);
      form.resetFields();
      dispatch(setSelectedSearch(query.searchId));
      dispatch(setSearchandFetchResults({ searchId: query.searchId, personId }));
      return;
    }
    if (!isObjectEmpty(updatedPayload)) {
      const payload = {
        FieldOptions: {
          ...initialSearch.FieldOptions,
          ...updatedPayload,
        },
      };
      const selectionTags = [];

      if (updatedPayload.SearchText) {
        const selectedNodes = {
          value: updatedPayload.SearchText,
          input: 'SearchText',
          children: updatedPayload.SearchText,
        };
        const tag = [updatedPayload.SearchText, selectedNodes, 'SearchText', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.States && Array.isArray(updatedPayload.States)) {
        const selectedStates = updatedPayload.States.map(s => s.StateCode);
        const selectedNodes = updatedPayload.States.map(s => {
          return {
            value: s.StateCode,
            children: s.StateDescription,
            input: 'States',
          };
        });
        const tag = [selectedStates, selectedNodes, 'States', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.Categories && Array.isArray(updatedPayload.Categories)) {
        if (!updatedPayload.Categories.length) return;

        const selectedNodes = {
          value: updatedPayload.Categories[0].CategoryId,
          input: 'Categories',
          children: updatedPayload.Categories[0].CategoryDescription,
        };
        const tag = [undefined, selectedNodes, 'Categories', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.Charts && Array.isArray(updatedPayload.Charts)) {
        const selectedCharts = updatedPayload.Charts.map(s => s.ChartId);
        const selectedNodes = updatedPayload.Charts.map(s => {
          return {
            value: s.ChartId,
            children: s.ChartTitle,
            input: 'Charts',
          };
        });
        const tag = [selectedCharts, selectedNodes, 'Charts', true];
        selectionTags.push(tag);
      }

      if (updatedPayload.DateStart && updatedPayload.DateEnd) {
        const dateRanges = [moment(updatedPayload.DateStart), moment(updatedPayload.DateEnd)];
        const selectedNodes = {
          value: dateRanges,
          children: `${updatedPayload.DateStart}-${updatedPayload.DateEnd}`,
          input: 'DateRanges',
        };
        const tag = [undefined, selectedNodes, 'DateRanges', true];
        selectionTags.push(tag);
      }

      const searchFiltersPayload = payload.FieldOptions;
      const formattedPayload = {
        SearchText: searchFiltersPayload.SearchText,
        Categories:
          Array.isArray(searchFiltersPayload.Categories) && searchFiltersPayload.Categories.length
            ? searchFiltersPayload.Categories.map(c => c.CategoryId)[0]
            : undefined,
        Charts:
          Array.isArray(searchFiltersPayload.Charts) && searchFiltersPayload.Charts.length
            ? searchFiltersPayload.Charts.map(c => c.ChartId)
            : [],
        States:
          Array.isArray(searchFiltersPayload.States) && searchFiltersPayload.States.length
            ? searchFiltersPayload.States.map(s => s.StateCode)
            : [],
        DateStart: searchFiltersPayload.DateStart,
        DateEnd: searchFiltersPayload.DateEnd,
      };

      setSelectionTags(selectionTags);
      dispatch(setSearchFilters(formattedPayload));
      dispatch(requestCharts({ payload }));
    }
  }, [queryString, fieldOptionString, asPath]);

  const onSearch = async () => {
    await validateFieldsAndScroll(async (err, values) => {
      if (err) return;
      const queryObj = {
        searchtext: values.SearchText,
        categories: values.Categories,
        charts: values.Charts.join(','),
        states: values.States.join(','),
        datestart: Array.isArray(values.DateRanges)
          ? convertMomentToDate(values.DateRanges[0])
          : null,
        dateend: Array.isArray(values.DateRanges)
          ? convertMomentToDate(values.DateRanges[1])
          : null,
      };
      const payload = search(values, FieldOptions);

      // Send GTM EVENT
      trackGTMEvent({
        event: 'complianceSearch',
        text: escapeGTMParam(values.SearchText || ''),
        state: values.States?.length > 0 ? values.States.join(', ') : 'All',
        category: values.Categories
          ? FieldOptions.Categories.filter(c => c.CategoryId === values.Categories)
            .map(c => escapeGTMParam(c.CategoryDescription))
            .join(', ')
          : 'All',
        chart:
          values.Charts?.length > 0
            ? FieldOptions.Charts.filter(c => (values.Charts || []).includes(c.ChartId))
              .map(c => escapeGTMParam(c.ChartTitle))
              .join(', ')
            : 'All',
      });

      const formattedURL = formatURL(rootSearchURL, queryObj);
      Router.push(formattedURL, formattedURL, { shallow: true });
      dispatch(setCurrentPage(1));
      dispatch(requestCharts({ payload }));
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (isSearchFiltersEmpty(form)) {
      notification.error({
        message: 'Search',
        description: 'Search option is required.',
      });
      return;
    }
    onSearch();
  };

  const handleFiltersCollapse = keys => {
    const isOpen = keys?.length > 0;

    trackGTMEvent({
      event: isOpen ? 'expandSearch' : 'collapseSearch',
    });

    handleFiltersCollapseChange(keys || []);
  };

  return (
    <Col xs={24} md={filterExpanded ? 24 : 12} lg={filterExpanded ? 24 : 8}>
      <Form onSubmit={handleSubmit}>
        <div css={style.filters} key={searchFilterKey}>
          <Collapse
            defaultActiveKey={['1']}
            {...filterCollapseProps}
            onChange={handleFiltersCollapse}
            css={filterColorOverride}
          >
            <Panel
              isActive
              key="1"
              defaultActiveKey={['1']}
              header={
                <Heading type={3} weight="bolder" theme="primaryGray">
                  Search Filters
                </Heading>
              }
              id={filterExpanded ? 'expandpanel' : 'collapsepanel'}
            >
              <Form.Item>
                <div className={style['ca-filter__search']} style={{ display: 'flex' }}>
                  {getFieldDecorator('SearchText', {
                    initialValue: searchFiltersValues.SearchText,
                  })(
                    <Input
                      prefix={<Icon type="search" />}
                      placeholder="Search"
                      css={formStyle.inputGray}
                      onChange={e => {
                        setSelection(e.target.value, null, 'SearchText');
                        updateSearch('SearchText', e.target.value);
                      }}
                    />
                  )}
                </div>
              </Form.Item>
              {renderSelect({
                placeholder: 'Select State',
                initialValue: searchFiltersValues.States,
                options: States,
                optId: 'StateCode',
                optDescription: 'StateDescription',
                fieldName: 'States',
                header: 'State',
                extra: 'Select one or more states.',
                extraProps: {
                  mode: 'multiple',
                },
              })}
              {renderSelect({
                placeholder: 'Select Category',
                initialValue: searchFiltersValues.Categories,
                options: Categories,
                optId: 'CategoryId',
                optDescription: 'CategoryDescription',
                fieldName: 'Categories',
                header: 'Category',
                extra: 'Select a category to see a list of available charts.',
                extraProps: {
                  onChange: (val, s) => {
                    handleCategoryChange(val);
                    setSelection(val, s, 'Categories');
                    updateSearch('Categories', val);
                  },
                },
              })}
              {renderSelect({
                placeholder: 'Select Chart',
                initialValue: searchFiltersValues.Charts,
                options: selectedCategoryValue ? filteredCharts : Charts,
                optId: 'ChartId',
                optDescription: 'ChartTitle',
                fieldName: 'Charts',
                header: 'Chart',
                extra: 'Select one or more charts.',
                extraProps: {
                  mode: 'multiple',
                },
              })}
              {includeDates && (
                <Collapse {...filterCollapseProps}>
                  <Panel
                    key="1"
                    header={
                      <Heading type={4} weight="bolder" theme="primaryGray">
                        Date Range
                      </Heading>
                    }
                  >
                    <Form.Item>
                      {form.getFieldDecorator('DateRanges', {
                        initialValue:
                          searchFiltersValues.DateStart && searchFiltersValues.DateEnd
                            ? [
                              moment(searchFiltersValues.DateStart),
                              moment(searchFiltersValues.DateEnd),
                            ]
                            : [null, null],
                        rules: [
                          {
                            message: 'Please complete start date and end date',
                            validator: validateCustomRange,
                          },
                        ],
                      })(
                        <DateRangePicker
                          size="large"
                          css={formStyle.inputGray}
                          onChange={(value, s) => {
                            setSelection(value, s, 'DateRanges');
                            updateSearch(
                              'DateStart',
                              Array.isArray(value) && convertMomentToDate(value[0])
                            );
                            updateSearch(
                              'DateEnd',
                              Array.isArray(value) && convertMomentToDate(value[1])
                            );
                          }}
                          disabledDate={disableDayAfter}
                        />
                      )}
                    </Form.Item>
                  </Panel>
                </Collapse>
              )}
            </Panel>
          </Collapse>
          <Buttons
            form={form}
            searchOptions={FieldOptions}
            btnDisabled={isLoadingOptions}
            handleSaveSearch={saveCompliassistSearch}
            handleClear={handleReset}
          />
        </div>
      </Form>
    </Col>
  );
};

SearchFilters.propTypes = {};

export default withRouter(SearchFilters);
